<template>
	<Teleport to="body">
		<Transition name="scrim">
			<div v-if="open" class="fixed inset-0 z-[90]" :style="{ background: 'rgb(var(--scrim) / var(--scrim-alpha))' }" aria-hidden="true" @click="close" />
		</Transition>
		<Transition name="sheet" @after-enter="focusSheet">
			<section
				v-if="open"
				ref="sheet"
				role="dialog"
				aria-modal="true"
				:aria-label="title || ariaLabel"
				tabindex="-1"
				class="sheet fixed inset-x-0 z-[91] flex flex-col rounded-t-[22px] bg-sheet text-ink outline-none"
				:class="{ 'sheet-tall': tall }"
				:style="dragStyle"
				@keydown.esc="close"
			>
				<div class="flex-none touch-none" @pointerdown="dragStart">
					<div class="mx-auto mt-2 h-[5px] w-10 rounded-full bg-line" />
					<slot name="header">
						<div class="flex h-14 items-center border-b border-line px-1.5">
							<div class="flex min-w-[72px] items-center"><slot name="left" /></div>
							<h2 class="min-w-0 flex-1 truncate text-center text-[17px] font-bold">{{ title }}</h2>
							<div class="flex min-w-[72px] justify-end">
								<button type="button" class="icon-btn" aria-label="Close" @click="close"><Icon name="close" :size="22" /></button>
							</div>
						</div>
					</slot>
				</div>
				<div class="min-h-0 flex-1 overflow-y-auto overscroll-contain" :class="bodyClass">
					<slot />
				</div>
				<div v-if="$slots.footer" class="flex-none border-t border-line px-4 pt-3" :style="{ paddingBottom: 'calc(12px + var(--sheet-safe-b))' }">
					<slot name="footer" />
				</div>
				<div v-else class="flex-none" :style="{ height: 'var(--sheet-safe-b)' }" />
			</section>
		</Transition>
	</Teleport>
</template>

<script>
import Icon from './Icon.vue'
import { closeSheet, isSheetOpen } from '@/lib/sheets'

let openCount = 0

export default {
	components: { Icon },
	props: {
		name: { type: String, required: true },
		title: { type: String, default: '' },
		ariaLabel: { type: String, default: '' },
		tall: { type: Boolean, default: false },
		bodyClass: { type: [String, Array, Object], default: 'px-5' },
	},
	emits: ['close', 'open'],
	data() {
		return { dragY: 0, dragging: false, startY: 0, startTime: 0, active: true }
	},
	computed: {
		open() {
			return this.active && isSheetOpen(this.$route, this.name)
		},
		dragStyle() {
			if (!this.dragY) return null
			return { transform: `translateY(${this.dragY}px)`, transition: this.dragging ? 'none' : undefined }
		},
	},
	watch: {
		open: {
			immediate: true,
			handler(value, previous) {
				if (value === !!previous) return
				openCount += value ? 1 : -1
				document.documentElement.classList.toggle('sheet-open', openCount > 0)
				this.$emit(value ? 'open' : 'close')
				if (!value) this.dragY = 0
			},
		},
	},
	// Pages kept in memory (KeepAlive) still have their sheets mounted; only the visible page's sheet opens.
	activated() {
		this.active = true
	},
	deactivated() {
		this.active = false
	},
	beforeUnmount() {
		if (this.open) {
			openCount -= 1
			document.documentElement.classList.toggle('sheet-open', openCount > 0)
		}
	},
	methods: {
		close() {
			closeSheet(this.name)
		},
		focusSheet() {
			this.$refs.sheet?.focus({ preventScroll: true })
		},
		// Swipe the grabber or header down to dismiss.
		dragStart(event) {
			if (event.target.closest('button, a, input')) return
			this.dragging = true
			this.startY = event.clientY
			this.startTime = Date.now()
			window.addEventListener('pointermove', this.dragMove)
			window.addEventListener('pointerup', this.dragEnd, { once: true })
			window.addEventListener('pointercancel', this.dragEnd, { once: true })
		},
		dragMove(event) {
			this.dragY = Math.max(0, event.clientY - this.startY)
		},
		dragEnd() {
			window.removeEventListener('pointermove', this.dragMove)
			const velocity = this.dragY / Math.max(1, Date.now() - this.startTime)
			this.dragging = false
			if (this.dragY > 110 || velocity > 0.6) this.close()
			else this.dragY = 0
		},
	},
}
</script>

<style scoped>
.sheet {
	/* Rides above the keyboard; the home-indicator inset is dropped while typing. */
	--sheet-safe-b: var(--safe-b);
	bottom: var(--kb);
	max-height: calc(100dvh - var(--kb) - var(--safe-t) - 24px);
	box-shadow: var(--shadow);
	transition: transform 0.24s cubic-bezier(0.2, 0.8, 0.2, 1);
}
:global(html.kb-open) .sheet {
	--sheet-safe-b: 0px;
}
.sheet-tall {
	height: calc(100dvh - var(--kb) - var(--safe-t) - 72px);
}
.sheet-enter-from,
.sheet-leave-to {
	transform: translateY(100%);
}
.sheet-leave-active {
	transition: transform 0.18s ease-in;
}
.scrim-enter-active,
.scrim-leave-active {
	transition: opacity 0.2s ease;
}
.scrim-enter-from,
.scrim-leave-to {
	opacity: 0;
}
</style>
